import {Button, Grid, Menu, MenuItem} from '@mui/material';
import React, {useEffect, useState} from 'react';
import AddIcon from '@mui/icons-material/Add';
import SwitchThemeButton from '../../components/other/switchThemeButton';
import {useWardDashboardStyles} from './wardDashboard';
import AgyleButton from '../../components/ui/buttons/agyleButton';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import AgyleSimpleSearchField from '../../components/ui/search/agyleSimpleSearchField';
import AgyleSelectField from '../../components/ui/selector/agyleSelectField';
import TuneIcon from '@mui/icons-material/Tune';
import {useTheme} from '@mui/material/styles';
import {useDispatch, useSelector} from 'react-redux';
import _, {isEmpty} from 'lodash';
import WardCard from './components/wardCard';
import {loadingWards, loadWardList, mapHospitalList, multiHospitalMode, wardsList} from '../../store/reducers/ward';
import InformationBox from '../../components/other/informationBox';
import useOpenState from '../../hooks/useOpenState';
import WardMapCreation from '../wardCreator/components/wardMapCreation';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import AgyleHeader from '../../components/header/agyleHeader/agyleHeader';
import AgyleLoadingSpinner from '../../components/other/agyleLoadingSpinner';
import {agylePref} from '../../store/reducers/preferences';
import moment from 'moment';
import {hasOneOfPermission, Permissions, user} from '../../store/reducers/user';
import API from '../../app/api';
import {setSnackbar} from '../../store/reducers/globals';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {useHistory} from 'react-router-dom';
import {createMapCode} from '../../util/mapCreator/mapUtils';
import WardAlerts from '../ward/map/components/wardAlerts';
import useWardDict from '../../hooks/useWardDict';
import {isCentralPageModuleEnabled} from '../centralPage/helper';
import AgyleAccordion from '../../components/ui/accordion/agyleAccordion';
import Typography from '@mui/material/Typography';

const DesktopWardDashboard = () => {
    const classes = useWardDashboardStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const {t} = useTranslation();
    const userObject = useSelector(user);
    const history = useHistory();

    const wards = useSelector(wardsList);
    const loadingMap = useSelector(loadingWards);
    const agylePreference = useSelector(agylePref);
    const isMultiHospitalMode = useSelector(multiHospitalMode);
    const hospitalList = useSelector(mapHospitalList);

    const {width: windowsWidth} = useWindowDimensions();

    // Create mode
    const canAccessMCT = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_ACCESS_MCT_ADMIN, Permissions.AGYLE_CAN_MANAGE_WARD_MAP, Permissions.AGYLE_CAN_PUBLISH_WARD_MAP, Permissions.AGYLE_CAN_VIEW_ONLY_WARD_MAP]));
    const canCopyDeleteMap = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_ACCESS_MCT_ADMIN, Permissions.AGYLE_CAN_MANAGE_WARD_MAP]));

    // States
    const [mapList, setMapList] = useState([]);
    const [mapType, setMapType] = useState('live');
    const [search, setSearch] = useState('');

    // Select state
    const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
    const [selectedMaps, setSelectedMaps] = useState([]);

    const [trudWardList] = useWardDict();
    const [wardsOpt, setWardsOpt] = useState([]);

    // Filter state
    const [filter, setFilter] = useState('all');
    const [filterByWard, setFilterByWard] = useState('');

    // Menus state
    const [menuEl, setMenuEl] = useState(null);
    const open = Boolean(menuEl);

    // New map state
    const [isNewMapOpen, openNewMap, closeNewMap] = useOpenState(false, true);

    // Accordion
    const [expanded, setExpanded] = useState(false);

    const FILTER_OPT = [
        {id: 'all', name: 'Show All', available: true},
        {id: 'favorite', name: 'My Favorites', available: true},
        {id: 'lastViewed', name: 'Last viewed be me', available: true},
        {id: 'lastEdited', name: 'Last edited be me', available: canAccessMCT},
        {id: 'lastPublished', name: 'Last published be me', available: canAccessMCT},
        {id: 'lastEditedAl', name: 'Last edited be all', available: true},
        {id: 'lastPublishedAl', name: 'Last published be all', available: true},
    ];

    useEffect(() => {
        if (!multiSelectEnabled) setSelectedMaps([]);
    }, [multiSelectEnabled]);

    useEffect(() => {
        let clone = _.cloneDeep(wards);
        const wardsId = new Set();

        // Map Wards
        _.forEach(clone, map => {
            if (!map.archived) {
                if (!canAccessMCT) {
                    if (map.active) wardsId.add(...map?.wardsId);
                } else {
                    wardsId.add(...map?.wardsId);
                }
            }
        });

        setWardsOpt(Array.from(wardsId));
        clone = _.filter(clone, ward => !ward.archived);

        // Ony live for normal user
        if (!canAccessMCT) clone = _.filter(clone, ward => ward.active);

        clone = filterMapsByMapType(clone, mapType);

        // Apply filterByWard
        if (filterByWard !== '') clone = _.filter(clone, ward => ward.wardsId?.includes(filterByWard));

        // Apply filter
        clone = filterMapsByFilter(clone, filter);

        // Filter by search text
        if (!_.isEmpty(search)) clone = _.filter(clone, ward => {
            const codes = ward.wardsId.map(code => code.toLowerCase());
            return ward.name.toLowerCase().includes(search.toLowerCase()) || codes.includes(search.toLowerCase());
        });

        const sortedList = _.sortBy(clone, obj => {
            return agylePreference?.wardMap?.favorites?.includes(obj?.__id) ? -1 : 1;
        });
        setMapList(sortedList);
    }, [wards, canAccessMCT, search, filter, filterByWard, mapType, agylePreference]);

    const filterMapsByFilter = (clone, filter) => {
        switch (filter) {
            case 'favorite': {
                const fav = [];
                if (!_.isEmpty(agylePreference?.wardMap?.favorites)) {
                    _.forEach(clone, map => {
                        if (agylePreference?.wardMap?.favorites?.includes(map?.__id)) fav.push(map);
                    });
                }
                return fav;
            }
            case 'lastViewed': {
                const maps = [];
                _.forEach(agylePreference?.wardMap?.lastVisit, id => {
                    const find = _.find(clone, {__id: id});
                    if (find) maps.push(find);
                });

                return maps;
            }
            case 'lastEdited':
            case 'lastEditedAl': {
                const edited = [];
                _.forEach(clone, map => {
                    if (moment(map?._lastModifiedDate).isBetween(moment().subtract(1, 'month'), moment())) {
                        if (filter === 'lastEditedAl') {
                            edited.push(map);
                        } else {
                            if (map?._lastModifiedBy === userObject?.userName) edited.push(map);
                        }
                    }
                });
                return edited;
            }
            case 'lastPublished':
            case 'lastPublishedAl': {
                const published = [];
                _.forEach(clone, map => {
                    if (map?.published && moment(map?.publishedDate).isBetween(moment().subtract(1, 'month'), moment())) {
                        if (filter === 'lastPublishedAl') {
                            published.push(map);
                        } else {
                            if (map?.publishedBy === userObject?.userName) published.push(map);
                        }
                    }
                });
                return published;
            }
            default:
                return clone;
        }
    };

    const filterMapsByMapType = (clone, mapType) => {
        switch (mapType) {
            case 'live':
                return _.filter(clone, map => map.active);
            case 'published':
                return _.filter(clone, map => !map.active && map.published);
            case 'draft':
                return _.filter(clone, map => !map.active && !map.published);
            case 'all':
            default:
                return clone;
        }
    };

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleChangeMapType = (type) => {
        setMapType(type);
    };

    const handleOpenMenu = (event) => {
        setMenuEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuEl(null);
    };

    const handleCloseMapCreation = () => {
        dispatch(loadWardList());
        closeNewMap();
    };

    const handleCountMapType = (typ) => {
        switch (typ) {
            case 'live': {
                const live = _.filter(wards, map => map.active);
                return live.length;
            }
            case 'published': {
                const published = _.filter(wards, map => !map.active && map.published);
                return published.length;
            }
            case 'draft': {
                const draft = _.filter(wards, map => !map.active && !map.published);
                return draft.length;
            }
            default:
                return wards.length;
        }
    };

    const getInfoText = () => {
        return _.isEmpty(search)
            ? t('The ward map list is empty, please contact your administrator to create or activate a new Ward Map.')
            : t('There are no wards map found for the selected filters.');
    };

    const cancelMultiSelect = () => {
        setMultiSelectEnabled(false);
    };

    const handleEditMap = () => {
        if (selectedMaps?.length !== 1) {
            dispatch(setSnackbar({type: 'info', message: t('Select the ward map which should be edited.')}));
        } else {
            const ward = _.find(mapList, {__id: selectedMaps[0]});
            if (ward?.code) history.push(`/wardCreator/${ward.code}`);
            else console.error(selectedMaps, 'No map found');
        }
    };

    const handleDuplicateMaps = () => {
        if (selectedMaps?.length > 0) {
            const clone = _.cloneDeep(mapList);
            let duplicate = [];
            _.forEach(selectedMaps, id => {
                const find = _.find(clone, {__id: id});
                if (id) duplicate.push(find);
            });

            _.forEach(duplicate, map => {
                map.__id = null;
                map.name += '_copy';

                map.active = false;
                map.activeBy = null;

                map.activeDate = null;
                map.published = false;
                map.publishedBy = null;
                map.publishedDate = null;

                map.code = createMapCode(map.wardsId, wards);

                API.updateWardMapList(map)
                    .catch(err => {
                        console.error(err);
                        dispatch(setSnackbar({
                            type: 'error',
                            message: 'There was a problem saving the map.',
                        }));
                    });
            });
            setMultiSelectEnabled(false);
            dispatch(setSnackbar({
                type: 'info',
                message: t('Maps has been successfully duplicated to drafts section.'),
            }));

            setTimeout(() => {
                dispatch(loadWardList());
            }, 1000);
        } else {
            dispatch(setSnackbar({
                type: 'info',
                message: t('To perform this operation, you must select at least one ward map.'),
            }));
        }
    };

    const handleDeleteMaps = () => {
        if (selectedMaps?.length > 0) {
            const clone = _.cloneDeep(mapList);
            let toDelete = [];
            _.forEach(selectedMaps, id => {
                const find = _.find(clone, {__id: id});
                if (id) toDelete.push(find);
            });

            _.forEach(toDelete, map => {
                if (map.active || map.published) {
                    map.archived = true;
                    map.archivedBy = userObject?.userName;
                    map.archivedDate = moment().toISOString();

                    API.updateWardMapList(map)
                        .catch(err => {
                            console.error(err);
                            dispatch(setSnackbar({
                                type: 'error',
                                message: 'There was a problem saving the map.',
                            }));
                        });
                } else {
                    API.deleteMap(map.code)
                        .catch(err => {
                            console.error(err);
                            dispatch(setSnackbar({
                                type: 'error',
                                message: 'There was a problem saving the map.',
                            }));
                        });
                }
            });
            setMultiSelectEnabled(false);
            dispatch(setSnackbar({
                type: 'info',
                message: t('Maps has been deleted.'),
            }));

            setTimeout(() => {
                dispatch(loadWardList());
            }, 1000);
        } else {
            dispatch(setSnackbar({
                type: 'info',
                message: t('To perform this operation, you must select at least one ward map.'),
            }));
        }
    };

    const handleSelectedMap = (id, add) => {
        let clone = _.cloneDeep(selectedMaps);

        if (add) clone.push(id);
        else clone = _.filter(clone, i => i !== id);

        setSelectedMaps(clone);
    };

    const renderHeader = () => {
        return (
            <>
                <AgyleHeader disableBack={!isCentralPageModuleEnabled}>
                    {!multiSelectEnabled &&
                        <Grid container direction="row" style={{paddingRight: 5}}>
                            {canAccessMCT &&
                                <>
                                    <div className={clsx(classes.rowDiv, 'marginLeft')} style={{gap: 5}}>
                                        <AgyleButton
                                            selected={mapType === 'live'}
                                            onClick={handleChangeMapType.bind(this, 'live')}
                                            size="small"
                                        >
                                            {t('Live')} {` ${handleCountMapType('live')}`}
                                        </AgyleButton>

                                        <AgyleButton
                                            selected={mapType === 'published'}
                                            onClick={handleChangeMapType.bind(this, 'published')}
                                            size="small"
                                        >
                                            {t('Published')} {` ${handleCountMapType('published')}`}
                                        </AgyleButton>

                                        <AgyleButton
                                            selected={mapType === 'draft'}
                                            onClick={handleChangeMapType.bind(this, 'draft')}
                                            size="small"
                                        >
                                            {t('Draft')} {` ${handleCountMapType('draft')}`}
                                        </AgyleButton>

                                        <AgyleButton
                                            selected={mapType === 'all'}
                                            onClick={handleChangeMapType.bind(this, 'all')}
                                            size="small"
                                        >
                                            {t('All')} {` ${handleCountMapType('all')}`}
                                        </AgyleButton>
                                    </div>

                                    <div className={clsx(classes.rowDiv, 'marginLeft')}>
                                        <AgyleButton
                                            onClick={openNewMap}
                                            variant="contained"
                                            startIcon={<AddIcon/>}
                                            size="small"
                                        >
                                            {t('New Map')}
                                        </AgyleButton>

                                        {canAccessMCT &&
                                            <AgyleButton
                                                onClick={() => setMultiSelectEnabled(true)}
                                                variant="outlined"
                                                colour="primary"
                                                size="small"
                                            >
                                                {t('Edit Mode')}
                                            </AgyleButton>
                                        }
                                    </div>
                                </>
                            }

                            <div className={clsx(classes.rowDiv, classes.right)}>
                                <AgyleSimpleSearchField
                                    value={search}
                                    onChange={setSearch}
                                    placeholder={t('Search ward maps...')}
                                />
                            </div>

                            <div className={clsx(classes.rowDiv, classes.right)}>
                                {windowsWidth > 1164 &&
                                    <AgyleSelectField
                                        onChange={setFilter}
                                        value={filter}
                                        options={_.filter(FILTER_OPT, {available: true})}
                                        defaultClearValue="all"
                                    />
                                }

                                <Button
                                    onClick={handleOpenMenu}
                                    className={classes.settingButton}
                                    color="inherit"
                                >
                                    <TuneIcon/>
                                </Button>

                                <div style={{marginLeft: 20}}>
                                    <SwitchThemeButton/>
                                </div>

                                <WardAlerts/>
                            </div>
                        </Grid>
                    }
                    {multiSelectEnabled &&
                        <div className={clsx(classes.rowDiv, classes.right)}>
                            <AgyleButton
                                disabled={selectedMaps?.length > 1}
                                onClick={handleEditMap}
                                variant="text"
                                size="small"
                            >
                                {t('Modify')}
                            </AgyleButton>

                            <AgyleButton
                                disabled={!canCopyDeleteMap}
                                onClick={handleDuplicateMaps}
                                variant="text"
                                size="small"
                            >
                                {t('Duplicate')}
                            </AgyleButton>

                            <AgyleButton
                                disabled={!canCopyDeleteMap}
                                onClick={handleDeleteMaps}
                                variant="text"
                                size="small"
                            >
                                {t('Delete')}
                            </AgyleButton>

                            <IconButton
                                disableRipple
                                onClick={cancelMultiSelect}
                            >
                                <CloseIcon/>
                            </IconButton>
                        </div>
                    }
                </AgyleHeader>

                <Menu
                    anchorEl={menuEl}
                    id={'setting-ward-menu'}
                    open={open}
                    onClose={handleCloseMenu}
                    PaperProps={{
                        style: {
                            borderRadius: 8,
                            backgroundColor: theme.palette.background.paper,
                            padding: '10px 20px',
                            width: 300,
                        },
                    }}
                    BackdropProps={{
                        classes: {
                            root: classes.backDrop,
                        },
                    }}
                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                    anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
                >
                    {windowsWidth <= 1164 &&
                        <div>
                            <Grid item xs={12} className={classes.menuLabel}>
                                {t('Filter maps')}
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleSelectField
                                    onChange={setFilter}
                                    value={filter}
                                    options={_.filter(FILTER_OPT, {available: true})}
                                    defaultClearValue="favorite"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider style={{margin: 5}}/>
                            </Grid>
                        </div>
                    }

                    <Grid item xs={12} className={classes.menuLabel}>
                        {t('Show maps for ward')}
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleSelectField
                            onChange={setFilterByWard}
                            value={filterByWard}
                            options={_.filter(trudWardList, ward => wardsOpt?.includes(ward?.wardCode)) || []}
                            customObject={renderWardOption}
                            withBorder
                            placeholder={t('Select department')}
                        />
                    </Grid>
                </Menu>
            </>

        );
    };

    const renderWardCards = () => {
        if (isEmpty(mapList)) return null;

        if (isMultiHospitalMode) {
            return hospitalList.map(hospital => {
                const id = hospital?.__id;
                const maps = mapList.filter(map => map?.hospitalId === id);

                return (
                    <AgyleAccordion
                        key={id}
                        expanded={expanded === id}
                        onChange={handleChange(id)}
                        label={_.startCase(hospital?.description?.toLowerCase())}
                        secondLabel={maps?.length || '0'}
                        headingStyle={classes.mapLabel}
                        secondHeadingStyle={classes.counter}
                    >
                        <Grid container direction="row">
                            {maps.map(ward => (
                                <Grid key={ward.__id} item sm={12} md={6} lg={3} xl={2}>
                                    <WardCard
                                        ward={ward}
                                        creatorMode={canAccessMCT}
                                        setMultiSelectEnabled={setMultiSelectEnabled}
                                        multiSelectEnabled={multiSelectEnabled}
                                        handleSelectedMap={handleSelectedMap}
                                    />
                                </Grid>
                            ))}
                            {isEmpty(maps) &&
                                <Typography variant="body1" fontWeight={600}>
                                    {t('No ward maps found for this hospital.')}
                                </Typography>
                            }
                        </Grid>
                    </AgyleAccordion>
                );
            });
        } else {
            return mapList.map(ward => (
                <Grid key={ward.__id} item sm={12} md={6} lg={3} xl={2}>
                    <WardCard
                        ward={ward}
                        creatorMode={canAccessMCT}
                        setMultiSelectEnabled={setMultiSelectEnabled}
                        multiSelectEnabled={multiSelectEnabled}
                        handleSelectedMap={handleSelectedMap}
                    />
                </Grid>
            ));
        }
    };

    return (
        <>
            {renderHeader()}

            {loadingMap && <AgyleLoadingSpinner/>}

            {!loadingMap &&
                <Grid
                    container
                    direction={isMultiHospitalMode ? 'column' : 'row'}
                    className={clsx('scroll-normal-white', classes.cards)}
                    style={{flexWrap: isMultiHospitalMode ? 'nowrap' : 'wrap'}}
                >
                    {(!isEmpty(mapList) && (_.startsWith(filter, 'last') && filter !== 'lastViewed')) &&
                        <div style={{margin: 10, width: '100%'}}>
                            <InformationBox type="info">
                                {t('The \'last\' option shows only the range of the last month.')}
                            </InformationBox>
                        </div>
                    }

                    {isEmpty(mapList) &&
                        <div style={{margin: 10}}>
                            <InformationBox type="info">
                                {getInfoText()}
                            </InformationBox>
                        </div>
                    }

                    {renderWardCards()}
                </Grid>
            }

            {isNewMapOpen &&
                <WardMapCreation
                    open={isNewMapOpen}
                    onClose={handleCloseMapCreation}
                    trudWardList={trudWardList}
                />
            }
        </>
    );
};

export default DesktopWardDashboard;

export const renderWardOption = (opt) => {
    return (
        <MenuItem
            key={opt?.__id}
            value={opt?.wardCode}
        >
            {_.startCase(opt?.wardName?.toLowerCase())}
        </MenuItem>
    );
};